import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Phone, X, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import axios from "axios";
import { catalog } from "../products";
import { shop } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (img) =>
  img && img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;

export default function CategoryPage() {
  const { slug } = useParams();
  const cat = catalog[slug];
  const [lightbox, setLightbox] = useState(null); // index or null
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/products/${slug}`)
      .then((r) => { if (active) setItems(r.data); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, items]);

  if (!cat) {
    return (
      <div className="min-h-screen bg-[#FCFAFB]">
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-44 pb-40 text-center">
          <h1 className="font-serif text-4xl text-[#123D5B]">Categoria non trovata</h1>
          <Link to="/#categorie" className="inline-flex items-center gap-2 text-[#C24E77] mt-6">
            <ArrowLeft size={16} /> Torna alle collezioni
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAFB]" data-testid={`category-page-${slug}`}>
      <Header />

      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-14 md:pb-16 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#F2A0B4]/30 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <Link
            to="/#categorie"
            data-testid="back-to-collections"
            className="inline-flex items-center gap-2 text-sm text-[#4A5863] hover:text-[#C24E77] transition-colors mb-7"
          >
            <ArrowLeft size={16} aria-hidden="true" /> Tutte le collezioni
          </Link>
          <div className="text-xs tracking-[0.2em] uppercase text-[#C24E77] mb-4">
            Reparto · {cat.title}
          </div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-[#123D5B] leading-[0.95]">
            {cat.title}
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#4A5863] max-w-2xl leading-relaxed">
            {cat.subtitle}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="py-24 text-center text-[#4A5863]">Caricamento prodotti…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#123D5B]/20 rounded-2xl py-20 text-center bg-white">
            <Camera className="mx-auto text-[#C24E77] mb-4" size={30} aria-hidden="true" />
            <h2 className="font-serif text-2xl text-[#123D5B]">Catalogo in arrivo</h2>
            <p className="text-[#4A5863] mt-2 max-w-md mx-auto">
              Stiamo fotografando i prodotti di questo reparto. Nel frattempo,
              passa in negozio: l'assortimento è vastissimo!
            </p>
            <a
              href={shop.phoneHref}
              className="inline-flex items-center gap-2 bg-[#123D5B] hover:bg-[#0C2A40] text-white rounded-sm px-7 py-3.5 mt-7 transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone size={16} aria-hidden="true" /> Chiedi disponibilità
            </a>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#4A5863] mb-8">
              {items.length} {items.length === 1 ? "articolo" : "articoli"} · tocca una foto per ingrandire
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {items.map((p, i) => (
                <motion.button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(i)}
                  data-testid={`product-card-${i}`}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                  className="group text-left bg-white rounded-2xl overflow-hidden border border-[#123D5B]/8 hover:shadow-[0_16px_44px_rgba(18,61,91,0.12)] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative overflow-hidden aspect-[3/4] bg-[#FBEFF3]">
                    <img
                      src={imgUrl(p.image)}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {p.price && (
                      <span className="absolute top-3 right-3 bg-[#123D5B] text-white text-sm font-500 rounded-full px-3 py-1">
                        {p.price}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-lg text-[#123D5B] leading-tight">{p.name}</h3>
                    {p.note && <p className="text-xs text-[#4A5863] mt-1">{p.note}</p>}
                  </div>
                </motion.button>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="product-lightbox"
            className="fixed inset-0 z-[60] bg-[#0C2A40]/92 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
              aria-label="Chiudi"
            >
              <X size={28} />
            </button>
            <button
              className="absolute left-4 md:left-8 text-white/70 hover:text-white p-2"
              onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + items.length) % items.length); }}
              aria-label="Precedente"
            >
              <ChevronLeft size={40} />
            </button>
            <button
              className="absolute right-4 md:right-8 text-white/70 hover:text-white p-2"
              onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % items.length); }}
              aria-label="Successivo"
            >
              <ChevronRight size={40} />
            </button>
            <motion.div
              key={lightbox}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imgUrl(items[lightbox].image)}
                alt={items[lightbox].name}
                className="w-full max-h-[76vh] object-contain rounded-lg"
              />
              <div className="text-center mt-4 text-white">
                <p className="font-serif text-2xl">{items[lightbox].name}</p>
                {items[lightbox].note && (
                  <p className="text-white/70 text-sm mt-1">{items[lightbox].note}</p>
                )}
                {items[lightbox].price && (
                  <p className="text-[#F2A0B4] text-lg mt-2">{items[lightbox].price}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
